import React from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import Message from '../../../base/react/components/web/Message';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { MESSAGE_TYPE_LOCAL } from '../../constants';
import { getFormattedTimestamp, getMessageText, getPrivateNoticeMessage } from '../../functions';
import PrivateMessageButton from './PrivateMessageButton';
import { useMemo } from "react";
import { Attachment } from "../../../../11sight/features/chat/components/attachments";
import { DownloadButton } from "../../../../11sight/features/chat/components/attachments/components/DownloadButton";
const useStyles = makeStyles()((theme, _, classes) => {
    const borderRadius = theme.spacing(2);
    return {
        chatMessageWrapper: {
            maxWidth: "100%",
            "&.local": {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end"
            }
        },
        chatMessage: {
            display: "inline-flex",
            maxWidth: "100%",
            marginTop: "4px",
            boxSizing: "border-box",
            color: theme.palette.iisightGray700,
            "&.privatemessage": {
                backgroundColor: theme.palette.support05
            },
            "&.local": {
                ".userMessage": {
                    backgroundColor: theme.palette.iisightGray100,
                    borderRadius: `${borderRadius} 0 ${borderRadius} ${borderRadius}`
                },
                "&.privatemessage": {
                    backgroundColor: theme.palette.support05
                }
            },
            "&.error": {
                borderRadius: 0,
                fontWeight: 100,
                [`& ${classes.userMessage}`]: {
                    backgroundColor: "rgb(215, 121, 118)"
                }
            },
            "&.lobbymessage": {
                backgroundColor: theme.palette.support05
            }
        },
        replyWrapper: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            maxWidth: "100%"
        },
        messageContent: {
            maxWidth: "100%",
            overflow: "visible",
            flex: 1
        },
        replyButtonContainer: {
            display: "flex",
            alignItems: "center",
            height: "100%",
            position: "absolute",
            top: "50%",
            right: "-35px",
            transform: "translateY(-50%)"
        },
        replyButton: {
            padding: "2px"
        },
        displayName: {
            ...withPixelLineHeight(theme.typography.labelBold),
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            marginBottom: theme.spacing(1)
        },
        userMessage: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            width: "fit-content",
            wordBreak: "break-word",
            padding: "12px",
            backgroundColor: theme.palette.iisightGreen100,
            borderRadius: `0 ${borderRadius} ${borderRadius} ${borderRadius}`,
            position: "relative",
            overflow: "visible"
        },
        privateMessageNotice: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text02,
            marginTop: theme.spacing(1)
        },
        timestamp: {
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.iisightGray400,
            marginTop: theme.spacing(1)
        },
        attachmentContainer: {
            height: "min-content",
            position: "relative",
            maxWidth: 190
        }
    };
});
/**
 * Renders a single chat message.
 *
 * @param {IProps} props - Component's props.
 * @returns {JSX}
 */
const ChatMessage = ({ knocking, message, showDisplayName, showTimestamp, type, t }) => {
    const { classes, cx } = useStyles();
    /**
     * Renders the display name of the sender.
     *
     * @returns {React$Element<*>}
     */
    function _renderDisplayName() {
        return (React.createElement("div", { "aria-hidden": true, className: cx('display-name', classes.displayName) }, message.displayName));
    }
    /**
     * Renders the message privacy notice.
     *
     * @returns {React$Element<*>}
     */
    function _renderPrivateNotice() {
        return (React.createElement("div", { className: classes.privateMessageNotice }, getPrivateNoticeMessage(message)));
    }
    /**
     * Renders the time at which the message was sent.
     *
     * @returns {React$Element<*>}
     */
    function _renderTimestamp() {
        return (React.createElement("div", { className: cx('timestamp', classes.timestamp) }, getFormattedTimestamp(message)));
    }
    let attachment = useMemo(() => {
        try {
            const parsedMessage = JSON.parse(message.message);
            if (typeof parsedMessage !== "object")
                return null;
            else
                return parsedMessage;
        }
        catch (err) {
            return null;
        }
    }, []);
    return ((React.createElement("div", { className: cx(classes.chatMessageWrapper, type), id: message.messageId, tabIndex: -1 },
        React.createElement("div", { className: cx("attachment-container", "chatmessage", classes.chatMessage, type) },
            React.createElement("div", { className: classes.replyWrapper },
                React.createElement("div", { className: cx("messagecontent", classes.messageContent) },
                    showDisplayName && _renderDisplayName(),
                    React.createElement("div", { className: cx("usermessage", classes.userMessage) },
                        React.createElement("span", { className: "sr-only" }, message.displayName === message.recipient ? t("chat.messageAccessibleTitleMe") : t("chat.messageAccessibleTitle", {
                            user: message.displayName
                        })),
                        attachment ? (React.createElement("div", { className: classes.attachmentContainer },
                            React.createElement(Attachment, { ...attachment }),
                            React.createElement(DownloadButton, { name: attachment.name, src: attachment.url }))) : (React.createElement(Message, { text: getMessageText(message) })),
                        (message.privateMessage || (message.lobbyChat && !knocking)) && message.messageType !== MESSAGE_TYPE_LOCAL && (React.createElement("div", { className: classes.replyButtonContainer },
                            React.createElement(PrivateMessageButton, { isLobbyMessage: message.lobbyChat, participantID: message.id }))))))),
        (message.privateMessage || (message.lobbyChat && !knocking)) && _renderPrivateNotice(),
        showTimestamp && _renderTimestamp())));
};
/**
 * Maps part of the Redux store to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    const { knocking } = state['features/lobby'];
    return {
        knocking
    };
}
export default translate(connect(_mapStateToProps)(ChatMessage));
